import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, memo, useEffect } from "react";

function lazyWithRetry<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory().catch((err) => {
      const reloadKey = `chunk_reload_${btoa(factory.toString().slice(0, 60)).replace(/[^a-z0-9]/gi, '')}`;
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
      }
      console.warn('Dynamic import failed after reload guard:', err);
      return new Promise<{ default: T }>(() => {});
    })
  );
}
import ErrorBoundary from "@/components/ErrorBoundary";
import PageLoader from "@/components/PageLoader";
import ProtectedRoute from "@/components/ProtectedRoute";
import { initAnalytics, identifyUser } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";

const SessionTimeoutGuard = () => {
  useSessionTimeout();
  return null;
};

// Lazy load all page components for code splitting
const Index = lazyWithRetry(() => import("./pages/Index"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Videos = lazyWithRetry(() => import("./pages/Videos"));
const EnhancedProfile = lazyWithRetry(() => import("./pages/EnhancedProfile"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const EnterpriseSetup = lazyWithRetry(() => import("./pages/EnterpriseSetup"));
const EnterpriseDashboard = lazyWithRetry(() => import("./pages/EnterpriseDashboard"));
const EnterpriseAnalytics = lazyWithRetry(() => import("./pages/EnterpriseAnalytics"));
const Players = lazyWithRetry(() => import("./pages/Players"));
const PlayerProfile = lazyWithRetry(() => import("./pages/PlayerProfile"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const HSLogin = lazyWithRetry(() => import("./pages/HSLogin"));
const EnterpriseLogin = lazyWithRetry(() => import("./pages/EnterpriseLogin"));
const HSProgramLogin = lazyWithRetry(() => import("./pages/HSProgramLogin"));
const HSProgramSetup = lazyWithRetry(() => import("./pages/HSProgramSetup"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const InstantEval = lazyWithRetry(() => import("./pages/InstantEval"));
const ConnectionTest = lazyWithRetry(() => import("./pages/ConnectionTest"));
const CheckEmail = lazyWithRetry(() => import("./pages/CheckEmail"));
const TransferPortal = lazyWithRetry(() => import("./pages/TransferPortal"));
const LearningDashboard = lazyWithRetry(() => import("./pages/LearningDashboard"));
const MarketOracle = lazyWithRetry(() => import("./pages/MarketOracle"));

// New public pages
const HowItWorks = lazyWithRetry(() => import("./pages/HowItWorks"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const SuccessStories = lazyWithRetry(() => import("./pages/SuccessStories"));
const EnterpriseSolutions = lazyWithRetry(() => import("./pages/EnterpriseSolutions"));
const RequestDemo = lazyWithRetry(() => import("./pages/RequestDemo"));
const CaseStudies = lazyWithRetry(() => import("./pages/CaseStudies"));
const ApiDocs = lazyWithRetry(() => import("./pages/ApiDocs"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const Support = lazyWithRetry(() => import("./pages/Support"));
const AboutUs = lazyWithRetry(() => import("./pages/AboutUs"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const EnterpriseRosterBuilder = lazyWithRetry(() => import("./pages/EnterpriseRosterBuilder"));
const BulkEvaluation = lazyWithRetry(() => import("./pages/BulkEvaluation"));
const RosterCanvas = lazyWithRetry(() => import("./components/enterprise/RosterCanvas"));
const EnterpriseLayout = lazyWithRetry(() => import("./components/enterprise/EnterpriseLayout"));
const RecruitingWarRoom = lazyWithRetry(() => import("./pages/RecruitingWarRoom"));
const BudgetScenarios = lazyWithRetry(() => import("./pages/BudgetScenarios"));
const ComplianceCenter = lazyWithRetry(() => import("./pages/ComplianceCenter"));
const BulkNILDealUpload = lazyWithRetry(() => import("./pages/BulkNILDealUpload"));
const OrganizationSettings = lazyWithRetry(() => import("./pages/OrganizationSettings"));
const EnterpriseUpgrade = lazyWithRetry(() => import("./pages/EnterpriseUpgrade"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const StatusPage = lazyWithRetry(() => import("./pages/StatusPage"));
const AdminStatusDashboard = lazyWithRetry(() => import("./pages/AdminStatusDashboard"));
const VerifySubscription = lazyWithRetry(() => import("./pages/VerifySubscription"));
const UnsubscribeStatus = lazyWithRetry(() => import("./pages/UnsubscribeStatus"));
const ScoreLanding = lazyWithRetry(() => import("./pages/ScoreLanding"));
const GoogleCalendarCallback = lazyWithRetry(() => import("./pages/GoogleCalendarCallback"));
const SalesforceCallback = lazyWithRetry(() => import("./pages/SalesforceCallback"));

// High School Athlete pages
const HighSchoolLanding = lazyWithRetry(() => import("./pages/HighSchoolLanding"));
const HSOnboarding = lazyWithRetry(() => import("./pages/HSOnboarding"));
const HSDashboard = lazyWithRetry(() => import("./pages/HSDashboard"));
const HSEditProfile = lazyWithRetry(() => import("./pages/HSEditProfile"));

// Configure QueryClient with production-ready defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// Memoized App component to prevent unnecessary re-renders
const App = memo(() => {
  // Initialize analytics on app startup
  useEffect(() => {
    initAnalytics();
    
    // Identify user if already authenticated
    const identifyCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });
      }
    };
    
    identifyCurrentUser();
    
    // Listen for auth changes to identify users on login
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });
      }
    });
    
    return () => subscription.unsubscribe();
  }, []);
  
  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <SessionTimeoutGuard />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/hs-login" element={<HSLogin />} />
              <Route path="/check-email" element={<CheckEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/verify/:token" element={<VerifySubscription />} />
              <Route path="/unsubscribe/:token" element={<UnsubscribeStatus />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/connection-test" element={<ConnectionTest />} />
              <Route path="/score" element={<ScoreLanding />} />
              
              {/* High School Athlete routes */}
              <Route path="/high-school" element={<HighSchoolLanding />} />
              <Route path="/hs-onboarding" element={
                <ProtectedRoute>
                  <HSOnboarding />
                </ProtectedRoute>
              } />
              <Route path="/hs-dashboard" element={
                <ProtectedRoute>
                  <HSDashboard />
                </ProtectedRoute>
              } />
              <Route path="/hs-profile/edit" element={
                <ProtectedRoute>
                  <HSEditProfile />
                </ProtectedRoute>
              } />
              {/* Admin routes */}
              <Route path="/admin/status" element={
                <ProtectedRoute>
                  <AdminStatusDashboard />
                </ProtectedRoute>
              } />
              
              {/* New public pages */}
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/success-stories" element={<SuccessStories />} />
              <Route path="/enterprise-solutions" element={<EnterpriseSolutions />} />
              <Route path="/enterprise" element={<Navigate to="/enterprise/dashboard" replace />} />
              <Route path="/request-demo" element={<RequestDemo />} />
              <Route path="/enterprise/login" element={<EnterpriseLogin />} />
              <Route path="/hs-program/login" element={<HSProgramLogin />} />
              <Route path="/hs-program/setup" element={<HSProgramSetup />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/api-docs" element={<ApiDocs />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/support" element={<Support />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/careers" element={<Careers />} />

              {/* Protected routes - require authentication and email verification */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              } />
              <Route path="/videos" element={
                <ProtectedRoute>
                  <Videos />
                </ProtectedRoute>
              } />
              <Route path="/enhanced-profile" element={
                <ProtectedRoute>
                  <EnhancedProfile />
                </ProtectedRoute>
              } />
              <Route path="/players" element={
                <ProtectedRoute>
                  <Players />
                </ProtectedRoute>
              } />
              <Route path="/player/:playerId" element={
                <ProtectedRoute>
                  <PlayerProfile />
                </ProtectedRoute>
              } />
              <Route path="/instant-eval" element={
                <ProtectedRoute>
                  <InstantEval />
                </ProtectedRoute>
              } />
              
              {/* Enterprise routes - protected */}
              <Route element={
                <ProtectedRoute>
                  <EnterpriseLayout />
                </ProtectedRoute>
              }>
              <Route path="/enterprise/setup" element={<EnterpriseSetup />} />
              <Route path="/enterprise/dashboard" element={<EnterpriseDashboard />} />
              <Route path="/enterprise/analytics" element={<EnterpriseAnalytics />} />
              <Route path="/enterprise/roster-builder" element={<EnterpriseRosterBuilder />} />
              <Route path="/enterprise/roster/:rosterId" element={<RosterCanvas />} />
              <Route path="/enterprise/evaluate" element={<BulkEvaluation />} />
              <Route path="/enterprise/war-room" element={<RecruitingWarRoom />} />
              <Route path="/enterprise/scenarios" element={<BudgetScenarios />} />
              <Route path="/enterprise/compliance" element={<ComplianceCenter />} />
              <Route path="/enterprise/bulk-deals" element={<BulkNILDealUpload />} />
              <Route path="/enterprise/settings" element={<OrganizationSettings />} />
              <Route path="/enterprise/upgrade" element={<EnterpriseUpgrade />} />
              <Route path="/enterprise/oauth/google-calendar" element={<GoogleCalendarCallback />} />
              <Route path="/enterprise/oauth/salesforce" element={<SalesforceCallback />} />
              </Route>
              <Route path="/transfer-portal" element={
                <ProtectedRoute>
                  <TransferPortal />
                </ProtectedRoute>
              } />
              <Route path="/learning" element={
                <ProtectedRoute>
                  <LearningDashboard />
                </ProtectedRoute>
              } />
              <Route path="/market-oracle" element={
                <ProtectedRoute>
                  <MarketOracle />
                </ProtectedRoute>
              } />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsentBanner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
});

App.displayName = 'App';

export default App;

